import axios from "axios";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const client = axios.create({ baseURL: BASE, withCredentials: true });

client.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    if (status === 401 && url.includes("/admin/") && window.location.pathname !== "/login") {
      window.location.assign(`/login?next=${encodeURIComponent(window.location.pathname)}`);
    }
    return Promise.reject(err);
  }
);

const unwrap = (p) => p.then((r) => r.data);

export const errorDetail = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return fallback;
};

export async function downloadFile(path, fallbackName) {
  let r;
  try {
    r = await client.get(path, { responseType: "blob" });
  } catch (e) {
    if (e?.response?.data instanceof Blob) {
      try { e.response.data = JSON.parse(await e.response.data.text()); } catch {}
    }
    throw e;
  }
  const cd = r.headers["content-disposition"] || "";
  const m = /filename\*=UTF-8''([^;]+)/.exec(cd) || /filename="?([^";]+)"?/.exec(cd);
  const name = m ? decodeURIComponent(m[1]) : fallbackName;
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
  return name;
}

export const api = {
  base: BASE,
  // auth
  login: (body) => unwrap(client.post("/auth/login", body)),
  logout: () => unwrap(client.post("/auth/logout")),
  me: () => unwrap(client.get("/auth/me")),
  systemStatus: () => unwrap(client.get("/admin/system/status")),
  // documents — indirme / dışa aktarma
  recentDocuments: (limit = 20) => unwrap(client.get("/admin/documents/recent", { params: { limit } })),
  downloadOriginal: (doc) => downloadFile(`/admin/documents/${doc.id}/download`, doc.filename),
  exportText: (doc) => downloadFile(`/admin/documents/${doc.id}/export`, `${doc.filename}.md`),
  downloadDatasetZip: (dataset, content = "original") =>
    downloadFile(`/admin/datasets/${dataset.id}/download?content=${content}`, `${dataset.name}.zip`),
  // org
  getOrg: () => unwrap(client.get("/admin/org")),
  getDataPolicy: () => unwrap(client.get("/admin/org/data-policy")),
  updateDataPolicy: (body) => unwrap(client.patch("/admin/org/data-policy", body)),
  // projects
  getProjects: () => unwrap(client.get("/admin/projects")),
  reindexStart: () => unwrap(client.post("/admin/reindex")),
  reindexStatus: () => unwrap(client.get("/admin/reindex/status")),
  createProject: (body) => unwrap(client.post("/admin/projects", body)),
  getProject: (pid) => unwrap(client.get(`/admin/projects/${pid}`)),
  deleteProject: (pid) => unwrap(client.delete(`/admin/projects/${pid}`)),
  getProjectAnalytics: (pid) => unwrap(client.get(`/admin/projects/${pid}/analytics`)),
  // datasets
  getDatasets: (pid) => unwrap(client.get(`/admin/projects/${pid}/datasets`)),
  createDataset: (pid, body) => unwrap(client.post(`/admin/projects/${pid}/datasets`, body)),
  getDataset: (did) => unwrap(client.get(`/admin/datasets/${did}`)),
  updateDataset: (did, body) => unwrap(client.patch(`/admin/datasets/${did}`, body)),
  deleteDataset: (did) => unwrap(client.delete(`/admin/datasets/${did}`)),
  // documents
  uploadDocument: (did, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return unwrap(client.post(`/admin/datasets/${did}/documents`, fd));
  },
  getDocuments: (did) => unwrap(client.get(`/admin/datasets/${did}/documents`)),
  getDocument: (id) => unwrap(client.get(`/admin/documents/${id}`)),
  getSegments: (id) => unwrap(client.get(`/admin/documents/${id}/segments`)),
  reprocess: (id) => unwrap(client.post(`/admin/documents/${id}/reprocess`)),
  deleteDocument: (id) => unwrap(client.delete(`/admin/documents/${id}`)),
  // assistants
  getAssistants: (pid) => unwrap(client.get(`/admin/projects/${pid}/assistants`)),
  createAssistant: (pid, body) => unwrap(client.post(`/admin/projects/${pid}/assistants`, body)),
  getAssistant: (aid) => unwrap(client.get(`/admin/assistants/${aid}`)),
  deleteAssistant: (aid) => unwrap(client.delete(`/admin/assistants/${aid}`)),
  updateAssistantConfig: (aid, config) => unwrap(client.patch(`/admin/assistants/${aid}`, { config })),
  setDatasets: (aid, dataset_ids) => unwrap(client.put(`/admin/assistants/${aid}/datasets`, { dataset_ids })),
  suggestQuestions: (aid) => unwrap(client.post(`/admin/assistants/${aid}/suggest-questions`)),
  uploadAssistantIcon: (aid, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return unwrap(client.post(`/admin/assistants/${aid}/icon`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }));
  },
  // playground
  playgroundChat: (aid, body) => unwrap(client.post(`/admin/assistants/${aid}/playground/chat`, body)),
  getChats: (aid) => unwrap(client.get(`/admin/assistants/${aid}/playground/chats`)),
  getChatMessages: (cid) => unwrap(client.get(`/admin/playground/chats/${cid}`)),
  feedback: (mid, body) => unwrap(client.post(`/admin/messages/${mid}/feedback`, body)),
  // publishing
  publish: (aid) => unwrap(client.post(`/admin/assistants/${aid}/publish`)),
  getPublications: (aid) => unwrap(client.get(`/admin/assistants/${aid}/publications`)),
  rollback: (pid) => unwrap(client.post(`/admin/publications/${pid}/rollback`)),
  // api keys
  createApiKey: (aid, body) => unwrap(client.post(`/admin/assistants/${aid}/api-keys`, body)),
  getApiKeys: (aid) => unwrap(client.get(`/admin/assistants/${aid}/api-keys`)),
  revokeApiKey: (id) => unwrap(client.post(`/admin/api-keys/${id}/revoke`)),
  // public
  publicMeta: (slug) => unwrap(client.get(`/public/assistants/${slug}`)),
  publicChat: (slug, body) => unwrap(client.post(`/public/assistants/${slug}/chat`, body)),
  // usage / audit
  getUsage: () => unwrap(client.get("/admin/usage")),
  getAudit: () => unwrap(client.get("/admin/audit")),
};

export default api;
